// EL CIERRE POR INACTIVIDAD, Y EL AVISO ANTES DE CERRAR.
//
// LO QUE HABÍA. Un solo número —60 minutos— contado desde que se entró y
// sin renovarse al trabajar. Es lo peor de las dos conductas: no protege
// del aparato abandonado en el pasillo, y sí le corta a media cuenta a
// quien lleva cincuenta y nueve minutos contando.
//
// LO QUE HAY AHORA. Lo que cierra la sesión es ESTAR QUIETO. Mientras se
// trabaja, la credencial se renueva sola contra el servidor.
//
// TRES REGLAS QUE NO SE PUEDEN ROMPER:
//
//   1. AVISAR ANTES, no después. Dos minutos de margen y un botón para
//      quedarse. Cerrar sin avisar, aunque sea correcto, se vive como que
//      el sistema se rompió.
//   2. EN TONO TRANQUILO, NO EN ROJO. Que la sesión se cierre es lo que
//      el sistema hace a propósito; no es culpa de quien estaba
//      trabajando. Es la misma corrección que se le hizo a la consola de
//      operador el 18 de agosto.
//   3. NO PERDER LO ESCRITO. Antes de cerrar se rescata (`borradores.js`)
//      y al volver a entrar se devuelve.
//
// LOS NÚMEROS LOS MANDA EL SERVIDOR. Aquí no hay ninguno escrito a mano:
// si lo hubiera, el día que alguien cambiara `INACTIVIDAD_MINUTOS` en el
// `.env` la pantalla seguiría avisando con el número viejo, y con toda la
// confianza.
import { useCallback, useEffect, useRef, useState } from 'react';

// Cuánto antes se avisa. Dos minutos: lo justo para volver de otra
// pestaña o de la impresora sin que el aviso se vuelva ruido de fondo.
export const MINUTOS_DE_AVISO = 2;

// Cada cuánto se mira el reloj. No es la precisión lo que manda —el
// margen es de minutos— sino no despertar la pestaña sin motivo.
//
// SE PUEDE CAMBIAR AL MONTAR, y solo lo hacen las pruebas. No es una
// puerta trasera: es que con cinco segundos clavados, cada paso de una
// prueba costaba cinco segundos REALES, el archivo entero tardaba medio
// minuto y las pruebas empezaban a morir por plantón en vez de por lo que
// dicen comprobar — que es la peor clase de prueba frágil, porque parece
// que encontró algo.
export const LATIDO_MS = 5000;

// Renovar la credencial cuesta una petición, así que no se hace en cada
// tecla: como mucho una vez por minuto, y solo si hubo actividad.
const RENOVAR_CADA_MS = 60_000;

// Los sucesos que cuentan como «aquí sigue habiendo alguien». `scroll` y
// `pointermove` van con captura y pasivos para no estorbar a nada.
const SEÑALES = ['pointerdown', 'pointermove', 'keydown', 'wheel',
                 'touchstart', 'scroll'];

/**
 * @param {object} opciones
 * @param {number} opciones.inactividadMinutos  cuánto se aguanta quieto
 * @param {boolean} opciones.activo             solo con sesión iniciada
 * @param {function} opciones.alCerrar          cerrar por inactividad
 * @param {function} opciones.renovar           alargar contra el servidor
 * @param {number} [opciones.latidoMs]           cada cuánto se mira el reloj
 */
export function useInactividad({ inactividadMinutos, activo, alCerrar,
                                 renovar, latidoMs = LATIDO_MS }) {
  const [segundosRestantes, setSegundosRestantes] = useState(null);
  // LOS RELOJES ARRANCAN EN 0 Y SE PONEN EN HORA AL MONTAR, no con
  // `Date.now()` en el render. Un render tiene que dar lo mismo siempre;
  // leer la hora ahí lo vuelve impredecible cuando React repite un render
  // —y el linter lo caza, con razón—.
  const ultimoMovimiento = useRef(0);
  const ultimaRenovacion = useRef(0);
  const huboActividad = useRef(false);
  // LA PESTAÑA SE FUE AL FONDO. Chrome CONGELA el `setInterval` de una
  // pestaña oculta; al volver, dispara un tic de recuperación con la hora
  // de hace un rato. Sin esto, ese tic calcularía «media hora quieto» y
  // cerraría la sesión de alguien que ACABA de volver a la pestaña. La
  // marca se pone al IRSE al fondo —un `visibilitychange` que el navegador
  // no estrangula, así que es fiable— y hace que el primer tic al volver
  // ponga el reloj en hora en lugar de cerrar. Ver el efecto del reloj.
  const reanudando = useRef(false);

  // «Sigo aquí»: reinicia el reloj y renueva ya, sin esperar al minuto.
  // Es también lo que se hace al volver al frente (visibilitychange→visible
  // y focus): volver a la pestaña ES estar aquí.
  const seguirAqui = useCallback(() => {
    reanudando.current = false;
    ultimoMovimiento.current = Date.now();
    ultimaRenovacion.current = Date.now();
    huboActividad.current = false;
    setSegundosRestantes(null);
    renovar?.();
    // Y LA PANTALLA SE ACTUALIZA SOLA · T-14. Renovar la credencial deja
    // la sesión viva, pero los datos en pantalla siguen siendo los de
    // hace media hora. Este aviso —uno solo, para toda la aplicación— es
    // por donde la sesión (`auth.jsx`) y cualquier pantalla que quiera
    // pueden volver a pedir sus datos al servidor sin recargar la página.
    // Se manda al VOLVER AL FRENTE y al pulsar «Sigo aquí», que es cuando
    // alguien regresa a mirar.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('tego:volvio-al-frente'));
    }
  }, [renovar]);

  // --- Escuchar que hay alguien delante ---
  useEffect(() => {
    if (!activo) return undefined;
    // Se ponen en hora aquí: al montar, no al renderizar.
    ultimoMovimiento.current = Date.now();
    ultimaRenovacion.current = Date.now();
    const marcar = () => {
      ultimoMovimiento.current = Date.now();
      huboActividad.current = true;
    };
    for (const s of SEÑALES) {
      window.addEventListener(s, marcar, { passive: true, capture: true });
    }
    return () => {
      for (const s of SEÑALES) {
        window.removeEventListener(s, marcar, { capture: true });
      }
    };
  }, [activo]);

  // --- Volver al frente es estar aquí, y no espera al tic ---
  //
  // Volver a la pestaña también es estar: sin esto, quien deja TEGO en
  // segundo plano mientras consulta otra cosa vuelve a un aviso — o, peor,
  // a una sesión ya cerrada. Y NO ESPERA AL SIGUIENTE TIC: renueva contra
  // el servidor EN EL ACTO. El tic viene del intervalo que Chrome tuvo
  // congelado y puede tardar hasta un latido en llegar; para entonces ya
  // habría cerrado. `seguirAqui` pone el reloj en hora y renueva ya.
  //
  // Al IRSE al fondo se marca `reanudando`, para que el tic de
  // recuperación —si se adelanta al `visibilitychange`— no cierre.
  useEffect(() => {
    if (!activo) return undefined;
    const alCambiarVisibilidad = () => {
      if (document.hidden) reanudando.current = true;   // se fue al fondo
      else seguirAqui();                                // volvió al frente
    };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('focus', seguirAqui);
    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('focus', seguirAqui);
    };
  }, [activo, seguirAqui]);

  // --- El reloj ---
  useEffect(() => {
    if (!activo || !inactividadMinutos) return undefined;
    const totalMs = inactividadMinutos * 60_000;
    const avisoMs = Math.min(MINUTOS_DE_AVISO * 60_000, totalMs / 2);

    const tic = setInterval(() => {
      // Si todavía no se puso en hora —el efecto de arriba no corrió—, no
      // se cuenta nada: valdría 56 años de inactividad y cerraría al
      // primer latido.
      if (!ultimoMovimiento.current) return;

      // CON LA PESTAÑA OCULTA NO SE CIERRA NI SE AVISA. No hay a quién
      // avisar, y Chrome estrangula o congela este intervalo mientras la
      // pestaña está al fondo: su cuenta no es de fiar. Se espera a que
      // vuelva al frente; el plazo del SERVIDOR es quien de verdad caduca
      // una pestaña abandonada al fondo (la renovación no llega si no hay
      // nadie, y a la vuelta el 401 cierra).
      if (typeof document !== 'undefined' && document.hidden) {
        setSegundosRestantes(null);
        return;
      }

      // PRIMER TIC TRAS VOLVER DEL FONDO. Puede ser el de recuperación del
      // intervalo que estuvo congelado, con la hora de hace un rato: se
      // pone el reloj en hora en vez de cerrar. La marca la puso el
      // `visibilitychange` al IRSE al fondo, que es fiable, así que esto
      // vale aunque el tic se adelante al `visibilitychange`→visible.
      if (reanudando.current) {
        reanudando.current = false;
        ultimoMovimiento.current = Date.now();
        ultimaRenovacion.current = Date.now();
        setSegundosRestantes(null);
        return;
      }

      const quieto = Date.now() - ultimoMovimiento.current;
      const queda = totalMs - quieto;

      if (queda <= 0) {
        setSegundosRestantes(0);
        alCerrar?.();
        return;
      }

      setSegundosRestantes(queda <= avisoMs ? Math.ceil(queda / 1000) : null);

      // RENOVAR SOLO SI HUBO ACTIVIDAD DE VERDAD. Renovar cada minuto
      // pasara lo que pasara convertiría el cierre por inactividad en
      // decoración: la credencial no caducaría nunca aunque no hubiera
      // nadie delante.
      if (huboActividad.current
          && Date.now() - ultimaRenovacion.current >= RENOVAR_CADA_MS) {
        huboActividad.current = false;
        ultimaRenovacion.current = Date.now();
        renovar?.();
      }
    }, latidoMs);

    return () => clearInterval(tic);
  }, [activo, inactividadMinutos, alCerrar, renovar, latidoMs]);

  return { segundosRestantes, seguirAqui };
}
